import './styles.scss';
import './styles.modules.scss';
import qs from 'querystring';
import { Tab, Tabs } from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import { HiLockClosed } from '@react-icons/all-files/hi/HiLockClosed';
// biome-ignore lint/style/useImportType: <explanation>
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { Button, Row } from 'reactstrap';
import { SERVICE_NAME } from '~constants/app';
import { REFERRAL_POINTS } from '~constants/trade';
// import { Video } from '~domain/file/Video';
import type { Product } from '~domain/product/Product';
import { accountSelector } from '~redux/account/selectors/account';
import type { AuthUser } from '~redux/auth/actions/authUser';
import { signin } from '~redux/auth/reducers/signin';
import { signup } from '~redux/auth/reducers/signup';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import type { Brand } from '~redux/brand/types';
import { makeSelectHasOrderedBrand } from '~redux/cart/selectors';
import { makeSelectCategory, makeSelectListPath } from '~redux/menu/selectors';
import { isProtectedBrand } from '~utils/brand';
import { formatCurrency } from '~utils/formatter';
import { pushViewItemEvent } from '~utils/gtm';
import { getPublicImageUrl } from '~utils/image';
import { productViewed } from '~utils/segment';
import CategoryProducts from '../CategoryProducts';
import InViewContainer from '../InViewContainer';
import Preview from '../Preview';
import ProductCartForm from '../ProductCartForm';
import ProductReviewList from '../ProductReviewList';
import ProductSlider from '../ProductSlider';
import RecentlyViewedProducts from '../RecentlyViewedProducts';
import RelatedProducts from '../RelatedProducts';
import SendMessage from '../SendMessage';
import SimilarProducts from '../SimilarProducts';

type ProductPostDetailsProps = {
  product: Product;
  preview?: boolean;
  admin?: boolean;
};

type PreviewData = {
  src: string;
  thumbnailUrl: string;
  type: 'image' | 'video';
};

const ProductPostDetails: React.FC<ProductPostDetailsProps> = ({
  product,
  preview,
  admin,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(authUserSelector);
  const is_direct = !!qs.parse(history.location.search?.split('?')?.[1])
    ?.direct;
  const contactId = qs.parse(history.location.search?.split('?')?.[1])
    ?.contactId;
  const account = useSelector(accountSelector);
  const category = useSelector(makeSelectCategory(product.product_category));
  const listPath = useSelector(
    makeSelectListPath(category, product.product_subcategory)
  );
  const sellOnlyDirect = useMemo(
    () => is_direct && product.brand.sell_only_direct,
    [is_direct, product.brand.sell_only_direct]
  );

  const [tab, setTab] = useState<'content' | 'size' | 'description'>('content');
  const handleTab = useCallback((_, tab) => setTab(tab), []);

  const retailPrice = formatCurrency(product.product_retail_price);

  const singlePrice = formatCurrency(product.unitPrice);

  const handleSignup = useCallback(() => {
    dispatch(
      signup({ brand: product.brand as Brand, contactId: contactId as string })
    );
  }, [product, contactId]);

  const handleLogin = useCallback(() => {
    dispatch(signin());
  }, []);

  const scrollToReviews = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const reviewList = document.getElementById(
        `review-list-${product.product_brand_id}`
      );
      if (reviewList) {
        reviewList.scrollIntoView({ block: 'center' });
      }
    },
    [product]
  );

  useEffect(() => {
    pushViewItemEvent(
      [
        {
          item_name: product.product_name,
          item_id: product.id,
          price: product.unitPrice,
          item_brand: product.brand.brand_name,
          item_category: product.product_category,
          item_category2: product.product_subcategory,
          index: 0,
          quantity: 1,
        },
      ],
      auth as AuthUser
    );
  }, []);

  useEffect(() => {
    if (!product) {
      return;
    }
    productViewed({
      brand: product.brand.brand_name,
      brand_id: product.product_brand_id,
      category: product.product_category,
      category2: product.product_subcategory,
      image_url: product.imageUrl,
      name: product.product_name,
      price: product.unitPrice,
      product_id: product.id,
      // quantity: 1,
      sku: product.product_number,
      url: `${document.location.protocol}//${document.location.host}/productdetail/${product.id}`,
      // variant: product.producttype?.[0]?.type_name ?? undefined,
    });
  }, [product]);

  useEffect(() => {
    if (auth || !is_direct || sellOnlyDirect) return;
    const timer = setTimeout(
      () =>
        dispatch(
          signup({
            contactId: contactId as string,
            brand: product.brand as Brand,
            referralId: product.brand.id,
          })
        ),
      3000
    );
    return () => {
      clearTimeout(timer);
    };
  }, [auth, is_direct, product, sellOnlyDirect]);

  const isProtected = useMemo(
    () =>
      !preview && account && isProtectedBrand(product.brand as Brand, account),
    [product, account, preview]
  );

  const [previews, setPreviews] = useState<PreviewData[]>([]);

  useEffect(() => {
    const exec = async () => {
      const images = product.imageKeys.map(({ imageUrl }) => ({
        src: imageUrl,
        thumbnailUrl: imageUrl,
        type: 'image' as const,
      }));

      const videos = await Promise.all(
        product.videos?.map(async ({ url }) => {
          // const thumbnail = await Video.generateThumbnailByUrl(url!);
          return {
            src: url!,
            thumbnailUrl: url!,
            type: 'video' as const,
          };
        }) ?? []
      );
      setPreviews([...images, ...videos]);
      // setPreviews([...images]);
    };

    exec();
  }, [product.imageKeys, product.videoKeys]);

  const isOrderedBrand = useSelector(
    makeSelectHasOrderedBrand(product.product_brand_id)
  );
  const isReturnable = useMemo(
    () => !isOrderedBrand && product.product_retail_price < 50000,
    [isOrderedBrand]
  );

  if (preview && !admin && auth?.attributes.sub !== product.product_owner) {
    return <Redirect to="/home" />;
  }

  if (!product.brand) {
    return null;
  }

  return (
    <>
      <div className="product-details">
        <div className="py-3 px-4 border-bottom text-muted">
          <Link to="/">Home</Link> /{' '}
          <Link
            to={`/brand/${product?.brand?.id}${location.search}`}
            translate="no"
          >
            {product?.brand?.brand_name}
          </Link>{' '}
          / {product?.product_name}
        </div>
        <div className="product-content-top single-product py-4">
          <div styleName="grid">
            <div styleName="grid-item header">
              <span className="product-content-name">
                <Link to={`/brand/${product.brand.id}${location.search}`}>
                  <div className="d-flex mb-4 align-items-center">
                    <div className="mr-2">
                      <img
                        className="rounded-circle mr-2 border"
                        src={getPublicImageUrl(
                          product.brand.brand_icon_imageKey ?? undefined,
                          {
                            w: 60,
                            h: 60,
                          }
                        )}
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                    <div>
                      <div className="font-weight-bold" translate="no">
                        {product.brand.brand_name}
                      </div>
                      {auth && (
                        <div className="text-black-50">
                          最小発注金額：
                          {product.brand.brand_minimum_buy
                            ? formatCurrency(product.brand.brand_minimum_buy)
                            : 'なし'}
                        </div>
                      )}
                      {!sellOnlyDirect &&
                        !!product.brand.reviews?.items?.length && (
                          <div className="d-flex">
                            <div>
                              <Rating value={1} max={1} readOnly />
                            </div>
                            <div>
                              <span>
                                {(
                                  product.brand.reviews.items.reduce(
                                    (prev, review) => prev + (review.rate ?? 0),
                                    0
                                  ) / product.brand.reviews.items.length
                                ).toFixed(1)}
                              </span>
                              <button
                                onClick={scrollToReviews}
                                className="bg-transparent border-0"
                                style={{ textDecoration: 'underline' }}
                              >
                                ({product.brand.reviews.items.length}
                                件のレビューを見る)
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </Link>
                <h3 className="product_title entry-title mb-0">
                  {product.product_name}
                </h3>
              </span>
            </div>
            <div styleName="grid-item preview" className="product-top-left">
              <div
                className="product-top-left-inner"
                styleName="product-top-left-inner"
              >
                <Preview images={previews} />
              </div>
            </div>
            <div styleName="grid-item meta" className="product_meta">
              <div
                className="tab-pane fade show active"
                id="description"
                role="tabpanel"
                aria-labelledby="home-tab"
              >
                <Tabs
                  className="border-bottom mb-4"
                  value={tab}
                  onChange={handleTab}
                  TabIndicatorProps={{ style: { background: 'black' } }}
                >
                  <Tab
                    label="商品概要"
                    className="font-weight-bold"
                    value={'content'}
                  />
                  <Tab
                    label="素材詳細"
                    className="font-weight-bold"
                    value={'description'}
                  />
                  <Tab
                    label="サイズ詳細"
                    className="font-weight-bold"
                    value={'size'}
                  />
                </Tabs>
                {tab === 'content' && (
                  <>
                    <div className="font-weight-bold">
                      <label>カテゴリー：{product.product_category}</label>
                    </div>
                    <div className="font-weight-bold">
                      <label>
                        ブランド：
                        <Link
                          to={`/brand/${product.brand.id}${location.search}`}
                          style={{ textDecoration: 'underline' }}
                          translate="no"
                        >
                          {product.brand.brand_name}
                        </Link>
                      </label>
                    </div>
                    {!isProtected && auth && (
                      <div className="font-weight-bold">
                        {product.product_preorder != 1 ? (
                          <label>
                            発送予定日：
                            {product.product_estimated_ship_date_min}〜
                            {product.product_estimated_ship_date_max}
                            営業日で発送予定
                          </label>
                        ) : (
                          <label>
                            発送予定日：{product.product_preorder_shipping_date}
                            に発送予定
                          </label>
                        )}
                      </div>
                    )}
                    <p
                      className="textarea-custom"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {product.product_content}
                    </p>
                    {!is_direct && (
                      <p className="textarea-custom mb-3">
                        販売元：{SERVICE_NAME}
                      </p>
                    )}
                  </>
                )}
                {tab === 'description' && (
                  <p
                    className="textarea-custom"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {product.product_description}
                  </p>
                )}
                {tab === 'size' && (
                  <p
                    className="textarea-custom"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {product.product_size}
                  </p>
                )}
                {!isProtected && !preview && auth && (
                  <div styleName="contacts-wrapper">
                    <SendMessage
                      brandId={product.product_brand_id}
                      productName={product.product_name}
                      styleName="brand-contact-link"
                    >
                      ブランドへの問い合わせ
                    </SendMessage>
                  </div>
                )}
              </div>
            </div>
            <div styleName="grid-item details" className="product-top-right">
              <div className="product-top-right-inner">
                <div className="summary entry-summary">
                  {isProtected && (
                    <small className="product-return-message">
                      ご利用の地域では現在発注できません。
                    </small>
                  )}
                  {auth && !isProtected && (
                    <>
                      <div className="single-price mb-4">
                        <div className="price">
                          卸価格：{singlePrice}（税抜）
                        </div>
                        <div className="mb-2">
                          <span>
                            希望小売価格：
                            {product.is_open_price
                              ? 'オープン価格'
                              : retailPrice}
                          </span>
                          {!product.is_open_price && (
                            <span>
                              （掛け率：
                              {product.productSaleWholesaleRate ? (
                                <>
                                  <span className="text-danger">
                                    {product.productSaleWholesaleRate}％
                                  </span>
                                  &nbsp;
                                  <del>{product.productWholesaleRate}％</del>
                                </>
                              ) : (
                                <>{product.productWholesaleRate}％</>
                              )}
                              ）
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mb-4">品番：{product.product_number}</div>
                    </>
                  )}
                  {!auth && (
                    <div styleName="public-details-wrapper">
                      <div styleName="public-details">
                        <div className="mb-4">
                          <p className="single-price mb-0">
                            <span className="price">
                              希望小売価格：{retailPrice}（税抜）
                            </span>
                          </p>
                          <a onClick={handleSignup}>
                            無料でバイヤー登録して卸価格を見る
                            <HiLockClosed />
                          </a>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2">
                            <Button styleName="signup" onClick={handleSignup}>
                              バイヤーアカウントを作成
                            </Button>
                          </div>
                          <div>
                            すでにアカウントをお持ちの方は
                            <a styleName="login" onClick={handleLogin}>
                              こちら
                            </a>
                            からログインください。
                          </div>
                        </div>
                        {!sellOnlyDirect && (
                          <div styleName="benefits">
                            <div>
                              <div styleName="benefits-title" className="mb-4">
                                {is_direct && (
                                  <div>
                                    <div className="mb-4">
                                      <b>
                                        <span translate="no">
                                          {product.brand.brand_name}
                                        </span>
                                        があなたを{SERVICE_NAME}
                                        へ招待しています！
                                      </b>
                                    </div>
                                    <div>
                                      こちらのページから会員登録すると、以下の特典があります！
                                    </div>
                                  </div>
                                )}
                                {!is_direct && (
                                  <div>
                                    <div className="mb-4">
                                      <b>
                                        {SERVICE_NAME}
                                        へ会員登録すると以下の特典がご利用いただけます！
                                      </b>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <ul>
                                <li>60日後の後払い</li>
                                <li>各ブランドとの初回取引は返品可能</li>
                              </ul>
                              <div styleName="terms">
                                ご登録の際は
                                <a
                                  href="https://www.homula.jp/terms/buyertermsofservice"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  ご利用規約
                                </a>
                                と
                                <a
                                  href="https://www.homula.jp/terms/privacypolicy"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  プライバシーポリシー
                                </a>
                                をご確認ください。
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!isProtected && auth && (
                    <>
                      <div className="mb-4">
                        <ProductCartForm
                          product={product}
                          productTypes={product.producttype}
                          isDirect={is_direct}
                          preview={preview}
                        />
                      </div>
                      {product.brand.terms_of_sample && (
                        <div className="mb-4">
                          <div className="font-weight-bold">
                            サンプルについて
                          </div>
                          <div>{product.brand.terms_of_sample}</div>
                        </div>
                      )}
                      {/* {!sellOnlyDirect && !preview && isReturnable && (
                        <div className="bg-orange p-4 rounded">
                          <h5 className="font-weight-bold">
                            初回購入ブランドは{SERVICE_NAME}へ返品できます。
                          </h5>
                          <div>
                            <span translate="no">
                              {product.brand.brand_name}
                            </span>
                            との初回お取引で購入した商品は{SERVICE_NAME}へ返品可能です。
                          </div>
                        </div>
                      )} */}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <RelatedProducts product={product} />
        </div>
      </div>
      {!preview && !is_direct && (
        <div className="py-5 px-md-5 border-top">
          <ProductReviewList
            brandId={product?.product_brand_id}
            productId={product?.id}
            containerClass="border-bottom mb-5"
          />
          {auth && (
            <InViewContainer placeholder={() => <ProductSlider isLoading />}>
              <SimilarProducts
                productId={product.id}
                brandId={product.product_brand_id}
              />
            </InViewContainer>
          )}
          <InViewContainer placeholder={() => <ProductSlider isLoading />}>
            <Row className={auth ? 'border-bottom mb-5' : ''}>
              <CategoryProducts
                category={category}
                subcategory={product.product_subcategory}
                listPath={listPath}
              />
            </Row>
          </InViewContainer>
          {auth && (
            <InViewContainer placeholder={() => <ProductSlider isLoading />}>
              <RecentlyViewedProducts />
            </InViewContainer>
          )}
        </div>
      )}
    </>
  );
};

export default ProductPostDetails;
